import React, { useState, useEffect, useMemo } from 'react';
import { MdCheckCircle, MdClose, MdWarning, MdInfo, MdSwapVert, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { userRequest } from '../utils/requestMethods';
import { toast } from 'react-toastify';
import { getClassesAhead, getClassesBehind } from '../utils/classHierarchy';
import ClassTransferConfirmationModal from './ClassTransferConfirmationModal';

// Skip-promote a student ahead of the normal next class, or demote a student
// back to an earlier class — for double promotions, skipped classes, repeats, etc.
const ClassTransferModal = ({
  isOpen,
  onClose,
  students = [],
  classInfo = {},
  allClasses = [],
  schoolCode,
  onTransferSuccess,
}) => {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [targetClassId, setTargetClassId] = useState('');
  const [checkResult, setCheckResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [studentsToTransfer, setStudentsToTransfer] = useState([]);
  // Only used for destinations off this class's chain, where ahead/behind
  // can't tell us which way the move goes.
  const [manualDirection, setManualDirection] = useState('promote');
  const [allFailedDetails, setAllFailedDetails] = useState([]);
  const [showDetailedFailed, setShowDetailedFailed] = useState(false);

  const eligibleStudents = students.filter(
    (student) => student.is_active && student.academic_status === 'active'
  );

  const aheadClasses = useMemo(
    () => getClassesAhead(allClasses, classInfo._id),
    [allClasses, classInfo._id]
  );
  const behindClasses = useMemo(
    () => getClassesBehind(allClasses, classInfo._id),
    [allClasses, classInfo._id]
  );

  // Classes not on this class's chain in either direction. `next_class` holds a
  // single id, so a school that streams (JSS 3 -> SS1 Science *or* SS1 Arts)
  // can only wire one branch; the other stream is unreachable by walking the
  // chain. Offering every other class is what makes those moves possible —
  // and skip-promotion is by definition a move outside the normal progression.
  const otherClasses = useMemo(() => {
    const onChain = new Set([...aheadClasses, ...behindClasses].map((c) => c._id));
    return (allClasses || [])
      .filter((c) => c._id !== classInfo._id && !onChain.has(c._id))
      .sort((a, b) => (a.class_name || '').localeCompare(b.class_name || ''));
  }, [allClasses, aheadClasses, behindClasses, classInfo._id]);

  const targetClass = useMemo(
    () => [...aheadClasses, ...behindClasses, ...otherClasses].find((c) => c._id === targetClassId) || null,
    [aheadClasses, behindClasses, otherClasses, targetClassId]
  );

  const isOffChain = Boolean(targetClassId) && otherClasses.some((c) => c._id === targetClassId);

  // On-chain moves infer their direction from which list the class came from;
  // off-chain ones can't, so the admin picks. The confirmation step restates it
  // either way before anything is sent.
  const direction = !targetClassId
    ? null
    : aheadClasses.some((c) => c._id === targetClassId)
      ? 'promote'
      : behindClasses.some((c) => c._id === targetClassId)
        ? 'demote'
        : manualDirection;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedStudents(eligibleStudents.length === 1 ? [eligibleStudents[0]._id] : []);
      setTargetClassId('');
      setManualDirection('promote');
      setCheckResult(false);
      setError('');
      setSuccess('');
      setAllFailedDetails([]);
      setShowDetailedFailed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleStudentSelection = (studentId, isSelected) => {
    setSelectedStudents((prev) =>
      isSelected ? [...prev, studentId] : prev.filter((id) => id !== studentId)
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === eligibleStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(eligibleStudents.map((student) => student._id));
    }
  };

  const allSelected = eligibleStudents.length > 0 && selectedStudents.length === eligibleStudents.length;
  const someSelected = selectedStudents.length > 0 && selectedStudents.length < eligibleStudents.length;

  const handleTransferClick = () => {
    if (selectedStudents.length === 0) {
      setError('Please select at least one student to move');
      return;
    }
    if (!targetClass) {
      setError('Please select a destination class');
      return;
    }

    const studentsData = eligibleStudents.filter((student) => selectedStudents.includes(student._id));
    setStudentsToTransfer(studentsData);
    setShowConfirmation(true);
  };

  const confirmTransfer = async () => {
    setLoading(true);
    setError('');
    setShowConfirmation(false);

    const isBulk = selectedStudents.length > 1;
    const isPromote = direction === 'promote';
    const destinationText = `to ${targetClass.class_name}`;

    try {
      let response;
      if (isPromote) {
        response = isBulk
          ? await userRequest.patch('/children/promote-students-to-class', {
              student_ids: selectedStudents,
              school_code: schoolCode,
              from_class_id: classInfo._id,
              target_class_id: targetClassId,
              check_result: checkResult,
            })
          : await userRequest.patch('/children/promote-student-to-class', {
              student_id: selectedStudents[0],
              school_code: schoolCode,
              from_class_id: classInfo._id,
              target_class_id: targetClassId,
              check_result: checkResult,
            });
      } else {
        response = isBulk
          ? await userRequest.patch('/children/demote-students', {
              student_ids: selectedStudents,
              school_code: schoolCode,
              target_class_id: targetClassId,
            })
          : await userRequest.patch('/children/demote-student', {
              student_id: selectedStudents[0],
              school_code: schoolCode,
              target_class_id: targetClassId,
            });
      }

      const data = response.data;
      const actionWord = isPromote ? 'promoted' : 'demoted';

      if (!isBulk) {
        // Single-student endpoints return a flat { child_id, child_name, message } object.
        const studentName = data.child_name || `${studentsToTransfer[0]?.firstname} ${studentsToTransfer[0]?.lastname}`;
        setSuccess(`✅ Successfully ${actionWord} ${studentName} ${destinationText}`);
        toast.success(`Successfully ${actionWord} ${studentName} ${destinationText}`);
        setTimeout(() => {
          onTransferSuccess();
          onClose();
        }, 2000);
        return;
      }

      // Bulk endpoints return { success: [...], failed: [...] }.
      const successCount = data.success?.length || 0;
      const failCount = data.failed?.length || 0;
      const maxDisplayItems = 5;

      setAllFailedDetails(failCount > maxDisplayItems ? data.failed || [] : []);

      let message = '';
      if (successCount > 0) {
        message += `✅ Successfully ${actionWord} ${successCount} student(s) ${destinationText}`;
        if (successCount <= maxDisplayItems) {
          message += ':\n' + data.success.map((s) => `• ${s.child_name || s.child_id}`).join('\n');
        } else {
          message += ':\n' + data.success.slice(0, maxDisplayItems).map((s) => `• ${s.child_name || s.child_id}`).join('\n');
          message += `\n  ... and ${successCount - maxDisplayItems} more`;
        }
      }
      if (failCount > 0) {
        if (message) message += '\n\n';
        message += `❌ Failed to ${direction} ${failCount} student(s):\n`;
        message += data.failed.slice(0, maxDisplayItems).map((f) => `• ${f.child_name || f.child_id}: ${f.message}`).join('\n');
        if (failCount > maxDisplayItems) {
          message += `\n  ... and ${failCount - maxDisplayItems} more (see full list below)`;
        }
      }
      if (successCount === 0 && failCount === 0) {
        message = 'No students were processed.';
      }
      setSuccess(message);

      if (successCount > 0) toast.success(`Successfully ${actionWord} ${successCount} student(s) ${destinationText}`);
      if (failCount > 0) toast.error(`Failed to ${direction} ${failCount} student(s). Check details above.`);

      if (successCount > 0) {
        setTimeout(() => {
          onTransferSuccess();
          onClose();
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${direction} student(s)`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasAnyTargets = aheadClasses.length > 0 || behindClasses.length > 0 || otherClasses.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-full">
              <MdSwapVert className="text-purple-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Move to Another Class</h2>
              <p className="text-sm text-gray-500">
                {classInfo?.class_name}
                {targetClass && (
                  <>
                    {' → '}
                    <span className="font-medium">{targetClass.class_name}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Info */}
          <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <MdInfo className="text-purple-600 mt-0.5" size={20} />
              <div className="text-sm text-purple-800">
                <p className="font-medium mb-1">Double / Skip Promotion &amp; Demotion</p>
                <ul className="space-y-1 text-purple-700">
                  <li>• Move students to any class ahead (skip promotion) or behind (demotion) their current class</li>
                  <li>• Use this for double promotions, correcting misplacements, or moving repeating students back</li>
                  <li>• Student records and academic history are preserved</li>
                </ul>
              </div>
            </div>
          </div>

          {!hasAnyTargets ? (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <MdWarning className="text-yellow-600 mt-0.5" size={20} />
                <p className="text-sm text-yellow-800">
                  {classInfo?.class_name} is the only class in this school, so there's nowhere to move students to yet.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Target class selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-900 mb-2">Destination class</label>
                <select
                  value={targetClassId}
                  onChange={(e) => setTargetClassId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                >
                  <option value="">Select a class...</option>
                  {aheadClasses.length > 0 && (
                    <optgroup label="Promote to (skip ahead)">
                      {aheadClasses.map((c) => (
                        <option key={c._id} value={c._id}>{c.class_name}</option>
                      ))}
                    </optgroup>
                  )}
                  {behindClasses.length > 0 && (
                    <optgroup label="Demote to (move back)">
                      {behindClasses.map((c) => (
                        <option key={c._id} value={c._id}>{c.class_name}</option>
                      ))}
                    </optgroup>
                  )}
                  {otherClasses.length > 0 && (
                    <optgroup label="Other classes (not on this progression)">
                      {otherClasses.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.class_name}{c.level_of_education ? ` — ${c.level_of_education}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>

                {isOffChain ? (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-1">Is this a promotion or a demotion?</p>
                    <p className="text-xs text-gray-500 mb-2">
                      {targetClass?.class_name} isn't linked to {classInfo?.class_name} in the class
                      progression, so we can't tell which way this move goes.
                    </p>
                    <div className="flex items-center gap-2">
                      {['promote', 'demote'].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setManualDirection(d)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                            manualDirection === d
                              ? d === 'promote'
                                ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : 'bg-amber-100 text-amber-700 border-amber-300'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {d === 'promote' ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
                          {d === 'promote' ? 'Promote' : 'Demote'}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : direction ? (
                  <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                    direction === 'promote' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {direction === 'promote' ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
                    {direction === 'promote' ? 'This will promote selected students' : 'This will demote selected students'}
                  </div>
                ) : null}
              </div>

              {/* Check Result Toggle - promotion only */}
              {direction === 'promote' && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 mb-1">Result Verification</h3>
                      <p className="text-sm text-gray-600">
                        {checkResult
                          ? 'Students will only be promoted if they have complete results for the current term/session'
                          : 'Students will be promoted regardless of result completion status'}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        type="button"
                        onClick={() => setCheckResult(!checkResult)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          checkResult ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            checkResult ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Error/Success Messages */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <MdWarning className="text-red-600 mt-0.5" size={20} />
                    <div className="text-red-800 whitespace-pre-line">{error}</div>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <MdCheckCircle className="text-green-600 mt-0.5" size={20} />
                    <div className="flex-1">
                      <div className="text-green-800 whitespace-pre-line">{success}</div>
                      {allFailedDetails.length > 0 && (
                        <div className="mt-3">
                          <button
                            onClick={() => setShowDetailedFailed(!showDetailedFailed)}
                            className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                          >
                            {showDetailedFailed ? 'Hide' : 'View'} All {allFailedDetails.length} Failed Students
                            <svg
                              className={`ml-1 w-4 h-4 transition-transform ${showDetailedFailed ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {showDetailedFailed && allFailedDetails.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-60 overflow-y-auto">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-red-800">All Failures ({allFailedDetails.length})</h4>
                    <button
                      onClick={() => setShowDetailedFailed(false)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      ✕ Close
                    </button>
                  </div>
                  <div className="space-y-2">
                    {allFailedDetails.map((failure, index) => (
                      <div key={failure.child_id || index} className="p-2 bg-white rounded border border-red-200">
                        <p className="text-sm font-medium text-gray-900">{failure.child_name || failure.child_id}</p>
                        <p className="text-xs text-red-600 mt-1">{failure.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bulk Selection */}
              {eligibleStudents.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="select-all-transfer"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="select-all-transfer" className="text-sm font-medium text-gray-700">
                        Select all eligible students ({eligibleStudents.length})
                      </label>
                    </div>
                    {selectedStudents.length > 0 && (
                      <button
                        onClick={handleTransferClick}
                        disabled={loading || !targetClass}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <MdSwapVert className="mr-2" size={16} />
                        )}
                        Move Selected ({selectedStudents.length})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Students List */}
              <div className="space-y-3">
                {eligibleStudents.length === 0 ? (
                  <div className="text-center py-8">
                    <MdWarning className="mx-auto text-gray-400 text-4xl mb-2" />
                    <p className="text-gray-500">No eligible students found</p>
                    <p className="text-sm text-gray-400 mt-1">Students must be active with 'active' academic status</p>
                  </div>
                ) : (
                  eligibleStudents.map((student) => (
                    <div key={student._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student._id)}
                          onChange={(e) => handleStudentSelection(student._id, e.target.checked)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-purple-600 font-medium text-sm">
                                {student.firstname?.[0]}{student.lastname?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{student.firstname} {student.lastname}</p>
                              <p className="text-sm text-gray-500">ID: {student.registration_id} • {student.gender}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {students.length > eligibleStudents.length && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <MdInfo className="inline mr-1" size={16} />
                    {students.length - eligibleStudents.length} student(s) are inactive and cannot be moved
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ClassTransferConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmTransfer}
        selectedStudents={studentsToTransfer}
        currentClass={classInfo}
        targetClass={targetClass}
        direction={direction}
        loading={loading}
      />
    </div>
  );
};

export default ClassTransferModal;
